export const SITE = {
  name: 'New Leaf Cleaning',
  tagline: 'Breathing life back into your home, one clean at a time.',
  taglineSub: 'Naturally clean. Thoughtfully made.',
  domain: 'nlcstpete.com',
  url: 'https://nlcstpete.com',
  ogImage: '/images/og-default.jpg',
  founded: 'December 2025',
  location: 'St. Petersburg, FL',
  serviceArea: 'Greater Tampa Bay',

  contact: {
    // No phone number is published: contact is email and the forms.
    email: 'hello@nlcstpete.com',
    address: 'St. Petersburg, FL 33713',
    hours: 'Mon\u2013Sat, 7 AM \u2013 6 PM',
  },

  hours: {
    weekday: 'Mon\u2013Fri: 8:00 am\u20136:00 pm',
    saturday: 'Saturday: 9:00 am\u20134:00 pm',
    sunday: 'Sunday: Closed',
    structured: 'Mo-Fr 08:00-18:00, Sa 09:00-16:00',
  },

  social: {
    yelp: 'https://www.yelp.com/biz/new-leaf-cleaning-st-petersburg',
    facebook: 'https://www.facebook.com/newleafcleaningservice',
    instagram: 'https://www.instagram.com/newleafcleaningservice',
    google: 'https://maps.app.goo.gl/VVCfSo87AAuCpYYM6',
  },

  geo: {
    lat: 27.793,
    lng: -82.6659,
  },

  // Legacy stats \u2014 kept for backward-compat with existing routes
  stats: {
    yelpRating: '5.0',
    yelpReviews: 4,
    responseTime: '< 10 min',
    communityGiving: '10%',
  },

  // reviews block used by new marketing routes
  reviews: {
    yelpRating: 5.0,
    yelpCount: 4,
    yelpUrl: 'https://www.yelp.com/biz/new-leaf-cleaning-st-petersburg',
  },

  // New marketing nav (Forest & Brass design)
  navLinks: [
    { label: 'Services',      href: '/services' },
    { label: 'Pricing',       href: '/pricing' },
    { label: 'About',         href: '/about' },
    { label: 'Reviews',       href: '/reviews' },
    { label: 'Service areas', href: '/service-areas' },
  ],

  footerLinks: {
    quickLinks: [
      { label: 'Home', href: '/' },
      { label: 'Services', href: '/services' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Get a Quote', href: '/quote' },
      { label: 'Service Areas', href: '/service-areas' },
      { label: 'About Us', href: '/about' },
      { label: 'Policies', href: '/policies' },
    ],
    portalLinks: [
      { label: 'Client Portal', href: '/client/login' },
      { label: 'Admin Login', href: '/admin/login' },
      { label: 'Employee Login', href: '/team/login' },
    ],
    legalLinks: [
      { label: 'Privacy Policy', href: '/policies#privacy' },
      { label: 'Terms of Service', href: '/policies#liability' },
    ],
  },
} as const

// ---------------------------------------------------------------------------
// Pricing — NEW LEAF'S OWN PUBLISHED CARD, and nobody else's.
//
// ⚠️ THIS MODULE IS ONE COMPANY'S MARKETING CONTENT. Everything below is New
// Leaf Cleaning's own published rate sheet, appearing on New Leaf's own site.
// That is the one legitimate use of a real company's numbers. It is NOT a
// platform default, and it must never be rendered on another tenant's surface.
//
// It used to read `loadRatesFromSettings({})` — the pricing loader called with
// NO TENANT AT ALL. Every key fell through to its hardcoded default, and those
// defaults are New Leaf's rates, so the file looked like it was "deriving from
// the engine" while actually hardcoding one company's card for every company
// the marketing site serves. An empty settings object cannot be a tenant lookup;
// naming the numbers is at least honest about what they are.
//
// The real fix — per-tenant marketing content — is an open decision, because
// these values are consumed from STATIC module scope by SEO metadata, JSON-LD
// and route descriptions (`lib/seo.ts`, `data/page-schemas.ts`,
// `data/route-metadata.ts`, `data/services.ts`, `data/home-content.ts`). Module
// scope in a Worker is shared across tenants, so a per-request value cannot live
// here without leaking between them. Those surfaces need a loader, and that is
// its own piece of work. Until then: New Leaf's site only.
//
// Before 2026-09-04 this file held its own hardcoded flat rate table and its own
// `sqft * rate` calculator. It silently diverged from the real engine and quoted
// prices the booking flow would not honour. Do not reintroduce that.
// ---------------------------------------------------------------------------

/**
 * New Leaf's published card, as shown on newleaf's own marketing pages. Band 1
 * rates — what the first `band1MaxSqft` square feet cost. Larger homes pay less
 * per square foot; any real number shown to a client comes from the engine via
 * `POST /api/pricing/calculate`, never from here.
 */
export const PRICING = {
  weeklyRate: 0.12,
  biweeklyRate: 0.14,
  monthly4Rate: 0.15,
  maintenanceRate: 0.17,
  fullDeepRate: 0.24,
  moveOutRate: 0.5,
  deepUpgradeRate: 0.07,
  minimum: 149,
  deepMinimum: 199,
  band1MaxSqft: 1500,
  cardMaxSqft: 6000,
} as const;
